/**
 * JourneyInsights — Tab 3 of SurveyDashboard
 *
 * Level 1: Study Overview (KPI strip, 5 charts, responder grid, exports)
 * Level 2: Responder Timeline (date-grouped access history, slide-in panel)
 * Level 3: Day Detail for a responder on a specific date
 * Day Overview Modal: all responders active on a date (Chart 2 date-click)
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import axios from 'axios';
import { API_URL } from '../runtimeConfig';
import { fetchAuthSession } from 'aws-amplify/auth';
import dayjs from 'dayjs';
import {
  BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell, Tooltip,
  XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer, FunnelChart, Funnel, LabelList,
} from 'recharts';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry, themeQuartz } from 'ag-grid-community';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

ModuleRegistry.registerModules([AllCommunityModule]);

const API = API_URL;

// ─── Colour palette ──────────────────────────────────────────────────────────
const TEAL   = '#008080';  // matches theme.colors.primary
const AMBER  = '#FFC107';  // matches theme.colors.warning
const INDIGO = '#2196F3';  // matches theme.colors.info
const ROSE   = '#E91E63';  // matches theme.colors.error
const PIE_COLORS = [TEAL, AMBER, INDIGO, ROSE, '#4CAF50', '#006666', '#FF8A65'];

// Recharts default tick fill is #666 — fine on white, too dark on dark card backgrounds.
// Apply to every XAxis/YAxis tick prop so all charts are consistent.
const AXIS_TICK = { fill: '#ffffff', fontSize: 12 };

const STATUS_COLORS = {
  SURVEY_DATA_CAPTURED:  { bg: '#d1fae5', text: '#065f46' },
  STARTED_NO_COMPLETION: { bg: '#ffedd5', text: '#c2410c' },
  ACCESSED_NO_SURVEY:    { bg: '#dbeafe', text: '#1d4ed8' },
  NEVER_ACCESSED:        { bg: '#f3f4f6', text: '#6b7280' },
};

const STATUS_HELP = {
  SURVEY_DATA_CAPTURED:  'Has completed at least one occasion (partial or fully). Survey data has been captured.',
  STARTED_NO_COMPLETION: 'Started survey blocks but has not completed any occasion yet.',
  ACCESSED_NO_SURVEY:    'Accessed the portal but has not started any survey block.',
  NEVER_ACCESSED:        'Has never clicked their unique survey link.',
};

// ─── Utility: download CSV ────────────────────────────────────────────────────
function downloadCSV(content, filename) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url  = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
}

function toCSV(rows, cols) {
  if (!rows?.length) return '';
  const header = cols.join(',');
  const body   = rows.map(r => cols.map(c => {
    const v = r[c] ?? '';
    return typeof v === 'string' && v.includes(',') ? `"${v}"` : v;
  }).join(','));
  return [header, ...body].join('\n');
}

function today() { return dayjs().format('YYYY-MM-DD'); }

// ─── Help Icon Tooltip ────────────────────────────────────────────────────────
function HelpIcon({ text }) {
  const [visible, setVisible] = useState(false);
  return (
    <HelpWrap
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <InfoOutlinedIcon style={{ fontSize: 16, color: '#9ca3af', cursor: 'help' }} />
      {visible && <HelpTooltip>{text}</HelpTooltip>}
    </HelpWrap>
  );
}

// ─── AG Grid Custom Header with Info Tooltip ──────────────────────────────────
// Tooltip renders via React portal into document.body — completely outside the
// AG Grid DOM tree and its overflow:hidden header cells. position:fixed +
// getBoundingClientRect() gives pixel-accurate placement regardless of any
// parent CSS constraints. Works on all screen sizes; no native browser delay.
function HeaderWithInfo(props) {
  const [tooltipPos, setTooltipPos] = useState(null);
  const iconRef = useRef(null);

  const show = () => {
    const r = iconRef.current.getBoundingClientRect();
    setTooltipPos({ x: r.left + r.width / 2, y: r.top - 6 });
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
      <span className="ag-header-cell-text">{props.displayName}</span>
      <span
        ref={iconRef}
        onMouseEnter={show}
        onMouseLeave={() => setTooltipPos(null)}
        style={{ cursor: 'help', lineHeight: 0, flexShrink: 0 }}
      >
        <InfoOutlinedIcon style={{ fontSize: 14, color: '#6b7280' }} />
      </span>
      {tooltipPos && createPortal(
        <div style={{
          position: 'fixed',
          left: tooltipPos.x,
          top: tooltipPos.y,
          transform: 'translate(-50%, -100%)',
          background: '#1f2937',
          color: '#f9fafb',
          fontSize: 12,
          lineHeight: 1.5,
          padding: '6px 10px',
          borderRadius: 6,
          maxWidth: 280,
          whiteSpace: 'normal',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          pointerEvents: 'none',
          zIndex: 99999,
        }}>
          {props.tooltip}
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.NEVER_ACCESSED;
  const label = status?.replace(/_/g, ' ') || '—';
  return (
    <BadgeWrap style={{ background: c.bg, color: c.text }}>
      {label}
      <HelpIcon text={STATUS_HELP[status] || label} />
    </BadgeWrap>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function JourneyInsights({ userAttributes }) {
  const gridRef = useRef(null);

  // ── State ──────────────────────────────────────────────────────────────────
  const [projects,    setProjects]    = useState([]);
  const [project, setProject]     = useState('');
  const [projectInfo, setProjectInfo] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError]         = useState(null);

  const [kpis,       setKpis]       = useState(null);
  const [funnel,     setFunnel]     = useState([]);
  const [daily,      setDaily]      = useState([]);
  const [retention,  setRetention]  = useState([]);
  const [heatmap,    setHeatmap]    = useState([]);
  const [device,     setDevice]     = useState([]);
  const [grid,       setGrid]       = useState([]);
  const [gridLoading, setGridLoading] = useState(false);

  // Level 2: Responder occasion drill-down
  const [selectedResponder, setSelectedResponder] = useState(null);
  const [occasions,         setOccasions]         = useState([]);
  const [occasionsLoading,  setOccasionsLoading]  = useState(false);

  // Day Overview Modal
  const [overviewDate,    setOverviewDate]    = useState(null);
  const [overviewData,    setOverviewData]    = useState([]);
  const [overviewLoading, setOverviewLoading] = useState(false);

  // ── Auth helper ────────────────────────────────────────────────────────────
  const getHeaders = useCallback(async () => {
    const session = await fetchAuthSession();
    const token   = session.tokens?.idToken?.toString();
    return { Authorization: `Bearer ${token}` };
  }, []);

  // ── Fetch project info (study period) when project changes ────────────────
  useEffect(() => {
    if (!project) { setProjectInfo(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const headers = await getHeaders();
        const res = await axios.get(`${API}/api/analytics-views/project-info/${project}`, { headers });
        if (!cancelled && res.data.success) setProjectInfo(res.data);
      } catch (err) {
        console.error('Failed to fetch project info:', err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [project, getHeaders]);

  // ── Fetch journey project list on mount ────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = await getHeaders();
        const res = await axios.get(`${API}/api/analytics-views/journey-projects`, { headers });
        if (!cancelled && res.data.success) {
          setProjects(res.data.projects || []);
        }
      } catch (err) {
        console.error('Failed to fetch journey projects:', err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [getHeaders]);

  // ── Fetch charts (funnel, daily, retention, heatmap, device) ─────────────
  const loadCharts = useCallback(async (proj) => {
    if (!proj) return;
    setLoading(true);
    setError(null);
    try {
      const headers = await getHeaders();
      const base    = `${API}/api/analytics-views`;
      const [f, d, r, h, dv] = await Promise.all([
        axios.get(`${base}/journey-funnel/${proj}`,             { headers }),
        axios.get(`${base}/journey-daily-engagement/${proj}`,   { headers }),
        axios.get(`${base}/journey-occasion-retention/${proj}`, { headers }),
        axios.get(`${base}/journey-heatmap/${proj}`,            { headers }),
        axios.get(`${base}/journey-device-breakdown/${proj}`,   { headers }),
      ]);
      setKpis(f.data.data.kpis);
      setFunnel(f.data.data.funnel);
      setDaily(d.data.data);
      setRetention(r.data.data);
      setHeatmap(h.data.data);
      setDevice(dv.data.data);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  // ── Fetch responder grid (slow — runs after charts) ────────────────────────
  const loadGrid = useCallback(async (proj) => {
    if (!proj) return;
    setGridLoading(true);
    setGrid([]);
    try {
      const headers = await getHeaders();
      const res = await axios.get(
        `${API}/api/analytics-views/journey-responder-grid/${proj}`,
        { headers }
      );
      setGrid(res.data.data || []);
    } catch (err) {
      console.error('Grid load failed:', err.message);
      setGrid([]);
    } finally {
      setGridLoading(false);
    }
  }, [getHeaders]);

  const loadData = useCallback((proj) => {
    loadCharts(proj);
    loadGrid(proj);   // fires in parallel — charts render without waiting for grid
  }, [loadCharts, loadGrid]);

  const handleLoad = () => {
    setSelectedResponder(null);
    loadData(project);
  };

  const handleRefresh = () => loadData(project);

  // ── Fetch Level 2: Responder Occasions ────────────────────────────────────
  const openResponder = useCallback(async (responderId) => {
    setSelectedResponder(responderId);
    setOccasions([]);
    setOccasionsLoading(true);
    try {
      const headers = await getHeaders();
      const res = await axios.get(
        `${API}/api/analytics-views/journey-responder-occasions/${project}/${responderId}`,
        { headers }
      );
      setOccasions(res.data.data || []);
    } catch (err) {
      console.error('Occasions fetch failed:', err.message);
    } finally {
      setOccasionsLoading(false);
    }
  }, [project, getHeaders]);

  // ── Fetch Day Overview (Chart 2 click) ────────────────────────────────────
  const openDayOverview = useCallback(async (date) => {
    setOverviewDate(date);
    setOverviewData([]);
    setOverviewLoading(true);
    try {
      const headers = await getHeaders();
      const res = await axios.get(
        `${API}/api/analytics-views/journey-day-overview/${project}`,
        { headers, params: { date } }
      );
      setOverviewData(res.data.data);
    } catch (err) {
      console.error('Day overview fetch failed:', err.message);
    } finally {
      setOverviewLoading(false);
    }
  }, [project, getHeaders]);

  // ── Exports ────────────────────────────────────────────────────────────────
  const exportFunnelCSV = () => {
    const csv = toCSV(funnel, ['stage', 'count', 'pct_of_enrolled']);
    downloadCSV(csv, `journey-funnel-${project}-${today()}.csv`);
  };
  const exportDailyCSV = () => {
    const csv = toCSV(daily, ['date', 'portal_accesses', 'block_completions']);
    downloadCSV(csv, `journey-daily-engagement-${project}-${today()}.csv`);
  };
  const exportRetentionCSV = () => {
    const csv = toCSV(retention, ['occasion_no', 'responders_started', 'responders_completed', 'completion_rate_pct']);
    downloadCSV(csv, `journey-occasion-retention-${project}-${today()}.csv`);
  };
  const exportHeatmapCSV = () => {
    const csv = toCSV(heatmap, ['day_of_week', 'hour_of_day', 'block_starts']);
    downloadCSV(csv, `journey-activity-heatmap-${project}-${today()}.csv`);
  };
  const exportDeviceCSV = () => {
    const csv = toCSV(device, ['dimension', 'category', 'count', 'pct']);
    downloadCSV(csv, `journey-device-breakdown-${project}-${today()}.csv`);
  };
  const exportGridAllCSV = () => {
    const cols = ['responder_id','first_portal_access','last_portal_access',
      'occasions_with_any_data','occasions_completed','last_completion_at','overall_journey_status'];
    const csv = toCSV(grid, cols);
    downloadCSV(csv, `journey-responder-grid-all-${project}-${today()}.csv`);
  };
  const exportGridSelectedCSV = () => {
    const selected = gridRef.current?.api?.getSelectedRows() || [];
    if (!selected.length) return;
    const cols = ['responder_id','enrolled_at','token_status','occasions_completed','overall_journey_status'];
    const csv = toCSV(selected, cols);
    downloadCSV(csv, `journey-responder-grid-selected-${project}-${today()}.csv`);
  };
  const exportAllDataCSV = () => {
    const parts = [];
    if (kpis) {
      parts.push('# KPI Summary');
      parts.push(`Enrolled,${kpis.enrolled}`);
      parts.push(`Accessed Portal,${kpis.accessed}`);
      parts.push(`Reached Welcome Screen,${kpis.welcomed}`);
      parts.push(`Started a Block,${kpis.started}`);
      parts.push(`Completed 1+ Occasion,${kpis.completed}`);
      parts.push(`Resume Events,${kpis.resume_checks}`);
      parts.push(`Start Fresh Choices,${kpis.start_fresh}`);
      parts.push('');
    }
    if (grid.length) {
      parts.push('# Responder Grid');
      parts.push(toCSV(grid, ['responder_id','enrolled_at','occasions_completed','overall_journey_status']));
    }
    downloadCSV(parts.join('\n'), `journey-all-data-${project}-${today()}.csv`);
  };
  const exportOccasionsCSV = () => {
    // Inject responder_id into each row so the downloaded CSV is self-contained
    const rows = occasions.map(o => ({ responder_id: selectedResponder, ...o }));
    const csv = toCSV(rows, ['responder_id','occasion_number','block_id','moment_status','started_at','last_updated_at']);
    downloadCSV(csv, `journey-responder-occasions-${selectedResponder}-${project}-${today()}.csv`);
  };
  const exportOverviewCSV = () => {
    const csv = toCSV(overviewData, ['responder_id', 'day_status']);
    downloadCSV(csv, `journey-day-overview-${overviewDate}-${project}-${today()}.csv`);
  };

  // ── AG Grid columns ────────────────────────────────────────────────────────
  const gridCols = [
    { field: 'responder_id',           headerName: 'Responder ID',        width: 180, pinned: 'left' },
    { field: 'first_portal_access',    headerName: 'First Access',         width: 150,
      valueFormatter: p => p.value ? dayjs(p.value).format('DD MMM YY HH:mm') : '—' },
    { field: 'last_portal_access',     headerName: 'Last Access',          width: 150,
      valueFormatter: p => p.value ? dayjs(p.value).format('DD MMM YY HH:mm') : '—' },
    { field: 'occasions_with_any_data', headerName: 'Responses Rcvd',     width: 140,
      headerComponent: HeaderWithInfo,
      headerComponentParams: { tooltip: 'Total (occasion × block) combinations recorded — Complete + Partial. Matches the row count in the detail panel when you double-click this row.' } },
    { field: 'occasions_completed',    headerName: 'Blocks Completed',    width: 150,
      headerComponent: HeaderWithInfo,
      headerComponentParams: { tooltip: '(Occasion × block) combinations where data was fully captured. For multi-block occasions, each block counts separately.' } },
    { field: 'last_completion_at',     headerName: 'Last Complete',        width: 150,
      valueFormatter: p => p.value ? dayjs(p.value).format('DD MMM YY HH:mm') : '—' },
    { field: 'overall_journey_status', headerName: 'Status',               width: 200,
      cellRenderer: p => <StatusBadge status={p.value} /> },
  ];

  // ── Heatmap helpers ────────────────────────────────────────────────────────
  const heatmapMax = heatmap.reduce((m, r) => Math.max(m, r.block_starts), 0);
  const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const heatmapGrid = Array.from({ length: 24 }, (_, h) =>
    DAYS.map((d, di) => {
      const cell = heatmap.find(r => r.dow_num === di && r.hour_of_day === h);
      return { day: d, hour: h, value: cell?.block_starts || 0 };
    })
  );
  function heatColor(val) {
    if (!heatmapMax) return '#f3f4f6';
    const pct = val / heatmapMax;
    if (pct === 0)   return '#f3f4f6';   // empty — neutral gray
    if (pct < 0.25)  return '#e0f2f0';   // very light teal
    if (pct < 0.5)   return '#4db6ac';   // mid teal
    if (pct < 0.75)  return '#008080';   // primary teal
    return '#006666';                     // dark teal
  }

  // ── Device pie helper ──────────────────────────────────────────────────────
  // Groups "Unknown"/null and anything below 1% of total into a single "Others" slice.
  // Industry standard: sub-1% slices add noise without insight.
  function deviceSlice(dimension) {
    const raw = device.filter(d => d.dimension === dimension);
    const total = raw.reduce((s, d) => s + d.count, 0);
    const main = [];
    let otherCount = 0;
    for (const d of raw) {
      const isUnknown = !d.category || d.category.toLowerCase() === 'unknown';
      const pctVal = total > 0 ? (d.count / total) * 100 : 0;
      if (isUnknown || pctVal < 1) {
        otherCount += d.count;
      } else {
        main.push({ name: d.category, value: d.count });
      }
    }
    if (otherCount > 0 && total > 0 && (otherCount / total) * 100 >= 1)
      main.push({ name: 'Others', value: otherCount });
    return main;
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  const hasData = funnel.length > 0 || kpis !== null;

  return (
    <Wrap>
      {/* ── Control Panel ── */}
      <ControlPanel>
        <FilterSection>
          <FilterLabel>Project:</FilterLabel>
          <FilterSelect value={project} onChange={e => setProject(e.target.value)}>
            <option value="">— Select project —</option>
            {projects.map(p => <option key={p} value={p}>{p}</option>)}
          </FilterSelect>
          <LoadBtn onClick={handleLoad} disabled={!project || loading}>
            {loading ? 'Loading…' : 'Load Data'}
          </LoadBtn>

          {projectInfo && (
            <ProjectDateBadge>
              <StatusDot active={projectInfo.status === 'active'} title={projectInfo.status} />
              <strong>{projectInfo.project_name}</strong>
              <span>&nbsp;|&nbsp;Study Period:&nbsp;</span>
              <strong>{String(projectInfo.start_date).substring(0, 10)} 00:00:00</strong>
              <span>&nbsp;→&nbsp;</span>
              <strong>{projectInfo.end_date ? `${String(projectInfo.end_date).substring(0, 10)} 23:00:00` : 'Open'}</strong>
            </ProjectDateBadge>
          )}
        </FilterSection>

        <ExportSection>
          <ActionBtn onClick={handleRefresh} disabled={!hasData || loading}>
            <RefreshIcon /> Refresh
          </ActionBtn>
          <ActionBtn onClick={exportAllDataCSV} disabled={!hasData}>
            <DownloadIcon /> Export All
          </ActionBtn>
          <ActionBtn onClick={exportFunnelCSV}    disabled={!funnel.length}><DownloadIcon /> Funnel</ActionBtn>
          <ActionBtn onClick={exportDailyCSV}     disabled={!daily.length}><DownloadIcon /> Daily</ActionBtn>
          <ActionBtn onClick={exportRetentionCSV} disabled={!retention.length}><DownloadIcon /> Retention</ActionBtn>
          <ActionBtn onClick={exportHeatmapCSV}   disabled={!heatmap.length}><DownloadIcon /> Heatmap</ActionBtn>
          <ActionBtn onClick={exportDeviceCSV}    disabled={!device.length}><DownloadIcon /> Devices</ActionBtn>
          <ActionBtn onClick={exportGridAllCSV}   disabled={!grid.length}><DownloadIcon /> Grid (All)</ActionBtn>
          <ActionBtn onClick={exportGridSelectedCSV} disabled={!grid.length}><DownloadIcon /> Grid (Sel)</ActionBtn>
        </ExportSection>
      </ControlPanel>

      {error && <ErrorBar>Error: {error}</ErrorBar>}

      {lastUpdated && (
        <StatusBar>
          Last refreshed: {lastUpdated.toLocaleString()} · Project: {project}
          {grid.length > 0 && ` · ${grid.length} responders`}
        </StatusBar>
      )}

      {!hasData && !loading && (
        <EmptyState>
          <EmptyIcon>📊</EmptyIcon>
          <EmptyText>Select a project and click "Load Data" to view journey insights.</EmptyText>
        </EmptyState>
      )}

      {loading && (
        <EmptyState>
          <EmptyIcon>⏳</EmptyIcon>
          <EmptyText>Loading journey data…</EmptyText>
        </EmptyState>
      )}

      {hasData && !loading && (
        <>
          {/* ── KPI Strip ── */}
          <KPIStrip>
            <KPICard>
              <KPILabel>Enrolled <HelpIcon text="Total responders invited to this study." /></KPILabel>
              <KPIValue>{kpis?.enrolled ?? '—'}</KPIValue>
            </KPICard>
            <KPICard>
              <KPILabel>Accessed Portal <HelpIcon text="Responders who clicked their unique link at least once." /></KPILabel>
              <KPIValue>{kpis?.accessed ?? '—'}</KPIValue>
              {kpis && <KPISub>{pct(kpis.accessed, kpis.enrolled)}% of enrolled</KPISub>}
            </KPICard>
            <KPICard>
              <KPILabel>Reached Welcome Screen <HelpIcon text="Opened the welcome screen and selected a language." /></KPILabel>
              <KPIValue>{kpis?.welcomed ?? '—'}</KPIValue>
              {kpis && <KPISub>{pct(kpis.welcomed, kpis.enrolled)}% of enrolled</KPISub>}
            </KPICard>
            <KPICard>
              <KPILabel>Started a Block <HelpIcon text="Began at least one survey moment." /></KPILabel>
              <KPIValue>{kpis?.started ?? '—'}</KPIValue>
              {kpis && <KPISub>{pct(kpis.started, kpis.enrolled)}% of enrolled</KPISub>}
            </KPICard>
            <KPICard>
              <KPILabel>Completed 1+ Occasion <HelpIcon text="Fully completed at least one survey occasion." /></KPILabel>
              <KPIValue>{kpis?.completed ?? '—'}</KPIValue>
              {kpis && <KPISub>{pct(kpis.completed, kpis.enrolled)}% of enrolled</KPISub>}
            </KPICard>
            <KPICard>
              <KPILabel>Resume Events <HelpIcon text="Times the app offered a resume option / times the responder chose to start fresh. Indicates session continuation patterns." /></KPILabel>
              <KPIValue>{kpis?.resume_checks ?? '—'} / {kpis?.start_fresh ?? '—'}</KPIValue>
              <KPISub>checks / start fresh</KPISub>
            </KPICard>
          </KPIStrip>

          {/* ── Charts Row 1: Funnel + Daily ── */}
          <ChartRow>
            <ChartCard>
              <ChartHeader>
                <ChartTitle>Engagement Funnel <HelpIcon text="How many responders progressed through each stage of the survey journey." /></ChartTitle>
              </ChartHeader>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={funnel} layout="vertical" margin={{ top: 5, right: 60, left: 0, bottom: 5 }}>
                  <XAxis type="number" domain={[0, 'dataMax']} allowDataOverflow tick={AXIS_TICK} />
                  <YAxis type="category" dataKey="stage" width={190} tick={AXIS_TICK} />
                  <Tooltip formatter={(v, n, p) => [`${v} (${p.payload.pct_of_enrolled}%)`, 'Responders']} />
                  <Bar dataKey="count" fill={TEAL} radius={[0, 4, 4, 0]} barSize={28}>
                    <LabelList dataKey="count" position="right" style={{ fontSize: 12, fill: '#555' }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard>
              <ChartHeader>
                <ChartTitle>Daily Engagement <HelpIcon text="Unique responders who accessed the portal vs completed a block each day. Click a date to see who was active." /></ChartTitle>
                <ChartSubtitle>Click a date to see active responders</ChartSubtitle>
              </ChartHeader>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={daily} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}
                  onClick={e => e?.activeLabel && openDayOverview(e.activeLabel)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" angle={-40} textAnchor="end" height={60} tick={{ ...AXIS_TICK, fontSize: 11 }}
                    tickFormatter={v => dayjs(v).format('DD MMM')} />
                  <YAxis tick={AXIS_TICK} />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="portal_accesses"   name="Portal Access"    fill={TEAL}  stroke={TEAL}  fillOpacity={0.3} />
                  <Area type="monotone" dataKey="block_completions"  name="Block Completions" fill={AMBER} stroke={AMBER} fillOpacity={0.3} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </ChartRow>

          {/* ── Charts Row 2: Retention + Heatmap ── */}
          <ChartRow>
            <ChartCard style={{ display: 'flex', flexDirection: 'column' }}>
              <ChartHeader>
                <ChartTitle>Occasion Retention <HelpIcon text="For each occasion number, how many responders started vs fully completed it." /></ChartTitle>
              </ChartHeader>
              <div style={{ flex: 1, minHeight: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={retention} margin={{ top: 10, right: 20, left: 10, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="occasion_no" tick={AXIS_TICK}
                      label={{ value: 'Occasion No.', position: 'insideBottom', offset: -15, fontSize: 11, fill: '#ffffff' }} />
                    <YAxis tick={AXIS_TICK} />
                    <Tooltip />
                    <Legend verticalAlign="bottom" wrapperStyle={{ paddingTop: 8 }} />
                    <Bar dataKey="responders_started"   name="Started"   fill={TEAL}  barSize={32} />
                    <Bar dataKey="responders_completed" name="Completed" fill={AMBER} barSize={32} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>

            <ChartCard>
              <ChartHeader>
                <ChartTitle>Activity Heatmap <HelpIcon text="When are responders most active? Colour intensity = number of block starts. Darker = more active." /></ChartTitle>
                <ChartSubtitle>Block starts by hour × day of week</ChartSubtitle>
              </ChartHeader>
              <HeatmapWrap>
                <HeatmapTable>
                  <thead>
                    <tr>
                      <HeatTh style={{ width: 36 }}>Hr</HeatTh>
                      {DAYS.map(d => <HeatTh key={d}>{d}</HeatTh>)}
                    </tr>
                  </thead>
                  <tbody>
                    {heatmapGrid.map((row, h) => (
                      <tr key={h}>
                        <HeatTh style={{ fontSize: 10 }}>{h.toString().padStart(2,'0')}</HeatTh>
                        {row.map((cell, di) => (
                          <HeatCell key={di} title={`${cell.day} ${h}:00 — ${cell.value} starts`}
                            style={{ background: heatColor(cell.value) }} />
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </HeatmapTable>
              </HeatmapWrap>
            </ChartCard>
          </ChartRow>

          {/* ── Chart 5: Device Breakdown ── */}
          <ChartCard style={{ marginBottom: 24 }}>
            <ChartHeader>
              <ChartTitle>Device & Platform Breakdown <HelpIcon text="How are responders accessing the survey platform? Based on device info collected at session start." /></ChartTitle>
            </ChartHeader>
            <DeviceRow>
              {[
                { key: 'os_platform',  label: 'OS / Platform'   },
                { key: 'form_factor',  label: 'Form Factor'     },
                { key: 'browser_type', label: 'Browser'         },
              ].filter(({ key }) => deviceSlice(key).length > 0).map(({ key, label }) => (
                <DevicePie key={key}>
                  <DevicePieLabel>{label}</DevicePieLabel>
                  <ResponsiveContainer width="100%" height={320}>
                    <PieChart>
                      <Pie data={deviceSlice(key)} dataKey="value" nameKey="name"
                           cx="50%" cy="50%" outerRadius={110}
                           label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                           labelLine>
                        {deviceSlice(key).map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v, n) => [v, n]} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </DevicePie>
              ))}
            </DeviceRow>
          </ChartCard>

          {/* ── Responder Summary Grid ── */}
          <SectionTitle>
            Responder Summary
            <HelpIcon text="One row per enrolled responder. Double-click a row to drill into their occasions and moment-level completion status." />
          </SectionTitle>
          <GridNote>Double-click any row to view occasion &amp; moment-level detail for that responder.</GridNote>
          {gridLoading ? (
            <EmptyState>
              <EmptyIcon>⏳</EmptyIcon>
              <EmptyText>Loading responder list…</EmptyText>
            </EmptyState>
          ) : (
            <GridWrap>
              <AgGridReact
                ref={gridRef}
                theme={themeQuartz}
                rowData={grid}
                columnDefs={gridCols}
                rowSelection="multiple"
                onRowDoubleClicked={e => openResponder(e.data.responder_id)}
                defaultColDef={{ resizable: true, sortable: true, filter: true }}
                domLayout="autoHeight"
                pagination
                paginationPageSize={50}
              />
            </GridWrap>
          )}
        </>
      )}

      {/* ── Level 2: Responder Occasion Drill-Down Panel ── */}
      {selectedResponder && (
        <SlidePanel>
          <PanelHeader>
            <PanelBack onClick={() => setSelectedResponder(null)}>
              <ArrowBackIcon /> Back to Grid
            </PanelBack>
            <PanelTitle>Responder: {selectedResponder}</PanelTitle>
            <ActionBtn onClick={exportOccasionsCSV} disabled={!occasions.length} style={{ marginLeft: 'auto' }}>
              <DownloadIcon /> Export
            </ActionBtn>
            <CloseBtn onClick={() => setSelectedResponder(null)}>
              <CloseIcon />
            </CloseBtn>
          </PanelHeader>

          {occasionsLoading ? (
            <PanelEmpty>Loading…</PanelEmpty>
          ) : occasions.length === 0 ? (
            <PanelEmpty>
              No survey data captured yet for this responder.
              Their status in the grid reflects their current stage.
            </PanelEmpty>
          ) : (() => {
            const completedOccs = occasions.filter(o => o.moment_status === 'Complete').length;
            const partialOccs   = occasions.filter(o => o.moment_status === 'Partial').length;
            return (
              <>
                <PanelHelp>
                  Responder: <strong>{selectedResponder}</strong>
                  &nbsp;·&nbsp;{occasions.length} row{occasions.length !== 1 ? 's' : ''} (one per occasion × block)
                  &nbsp;·&nbsp;<strong>{completedOccs} Complete</strong>
                  &nbsp;· {partialOccs} Partial · earliest first
                </PanelHelp>
                <div style={{ overflowX: 'auto' }}>
                <MomentTable>
                  <thead>
                    <tr>
                      <DayTh>Responder ID</DayTh>
                      <DayTh>Occasion</DayTh>
                      <DayTh>Block ID</DayTh>
                      <DayTh>Status</DayTh>
                      <DayTh>First Started</DayTh>
                      <DayTh>Last Updated</DayTh>
                    </tr>
                  </thead>
                  <tbody>
                    {occasions.map((m, i) => (
                      <tr key={i}>
                        <DayTd style={{ color: '#6b7280', fontSize: 11 }}>{selectedResponder}</DayTd>
                        <DayTd>{m.occasion_number ?? '—'}</DayTd>
                        <DayTd>{m.block_id || '—'}</DayTd>
                        <DayTd>
                          <span style={{
                            fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 12,
                            background: m.moment_status === 'Complete' ? '#d1fae5' : '#ffedd5',
                            color:      m.moment_status === 'Complete' ? '#065f46' : '#92400e',
                          }}>
                            {m.moment_status}
                          </span>
                        </DayTd>
                        <DayTd>{m.started_at     ? dayjs(m.started_at).format('DD MMM YY HH:mm')     : '—'}</DayTd>
                        <DayTd>{m.last_updated_at ? dayjs(m.last_updated_at).format('DD MMM YY HH:mm') : '—'}</DayTd>
                      </tr>
                    ))}
                  </tbody>
                </MomentTable>
                </div>
              </>
            );
          })()}
        </SlidePanel>
      )}

      {/* ── Day Overview Modal (Chart 2 click) ── */}
      {overviewDate && (
        <ModalOverlayWrap>
          <ModalBg onClick={() => setOverviewDate(null)} />
          <ModalBox>
            <ModalHeader>
              <ModalTitle>Active responders on {dayjs(overviewDate).format('dddd, DD MMMM YYYY')}</ModalTitle>
              <ActionBtn onClick={exportOverviewCSV} disabled={!overviewData.length}>
                <DownloadIcon /> Export
              </ActionBtn>
              <CloseBtn onClick={() => setOverviewDate(null)}><CloseIcon /></CloseBtn>
            </ModalHeader>
            {overviewLoading ? (
              <PanelEmpty>Loading…</PanelEmpty>
            ) : overviewData.length === 0 ? (
              <PanelEmpty>No data for this date.</PanelEmpty>
            ) : (
              <OverviewTable>
                <thead>
                  <tr>
                    <DayTh>Responder ID</DayTh>
                    <DayTh>Activity</DayTh>
                    <DayTh>Action</DayTh>
                  </tr>
                </thead>
                <tbody>
                  {overviewData.map((r, i) => (
                    <tr key={i}>
                      <DayTd>{r.responder_id}</DayTd>
                      <DayTd>
                        <span style={{ color: r.day_status === 'Also Completed' ? '#065f46' : '#92400e', fontWeight: 600 }}>
                          {r.day_status}
                        </span>
                      </DayTd>
                      <DayTd>
                        <LinkBtn onClick={() => { setOverviewDate(null); openResponder(r.responder_id); }}>
                          View Timeline →
                        </LinkBtn>
                      </DayTd>
                    </tr>
                  ))}
                </tbody>
              </OverviewTable>
            )}
          </ModalBox>
        </ModalOverlayWrap>
      )}
    </Wrap>
  );
}

function pct(num, den) {
  if (!den) return 0;
  return ((num / den) * 100).toFixed(1);
}

// ─── Styled Components ────────────────────────────────────────────────────────

const Wrap = styled.div`position: relative;`;

const ControlPanel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: ${p => p.theme.spacing.md};
  padding: ${p => p.theme.spacing.md};
  background: ${p => p.theme.colors.cardBackground};
  border-radius: ${p => p.theme.borderRadius.lg};
  border: 1px solid ${p => p.theme.colors.border};
  margin-bottom: ${p => p.theme.spacing.md};
`;

const ProjectDateBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${p => p.theme.colors.textSecondary};
  background: ${p => p.theme.colors.surface || p.theme.colors.cardBackground};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 4px;
  padding: 4px 10px;
  white-space: nowrap;
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => p.active ? '#22c55e' : '#f59e0b'};
  margin-right: 2px;
  flex-shrink: 0;
`;

const FilterSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  flex-wrap: wrap;
`;

const FilterLabel = styled.label`
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: ${p => p.theme.fontWeights.medium};
  color: ${p => p.theme.colors.textSecondary};
`;

const FilterSelect = styled.select`
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  border-radius: ${p => p.theme.borderRadius.md};
  border: 1px solid ${p => p.theme.colors.border};
  background: white;
  font-size: ${p => p.theme.fontSizes.sm};
  min-width: 180px;
`;

const ExportSection = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.sm};
  flex-wrap: wrap;
`;

const ActionBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  border-radius: ${p => p.theme.borderRadius.md};
  border: none;
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: ${p => p.theme.fontWeights.semibold};
  cursor: pointer;
  background: ${p => p.theme.colors.primary};
  color: white;
  transition: all ${p => p.theme.transitions.fast};
  &:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const LoadBtn = styled(ActionBtn)``;

const StatusBar = styled.div`
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textSecondary};
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  background: ${p => p.theme.colors.cardBackground};
  border-radius: ${p => p.theme.borderRadius.md};
  border: 1px solid ${p => p.theme.colors.border};
  margin-bottom: ${p => p.theme.spacing.md};
`;

const ErrorBar = styled.div`
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  background: #fef2f2;
  color: #dc2626;
  border-radius: ${p => p.theme.borderRadius.md};
  margin-bottom: ${p => p.theme.spacing.md};
  font-size: ${p => p.theme.fontSizes.sm};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${p => p.theme.spacing['3xl']};
  background: ${p => p.theme.colors.cardBackground};
  border-radius: ${p => p.theme.borderRadius.lg};
`;

const EmptyIcon = styled.div`font-size: 56px; margin-bottom: ${p => p.theme.spacing.md};`;
const EmptyText = styled.p`
  font-size: ${p => p.theme.fontSizes.lg};
  color: ${p => p.theme.colors.textSecondary};
`;

// KPI Strip
const KPIStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: ${p => p.theme.spacing.md};
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const KPICard = styled.div`
  background: ${p => p.accent ? '#f0fdfa' : p.theme.colors.cardBackground};
  border: 1px solid ${p => p.accent ? '#99f6e4' : p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.md};
`;

const KPILabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textSecondary};
  font-weight: ${p => p.theme.fontWeights.medium};
  margin-bottom: 4px;
`;

const KPIValue = styled.div`
  font-size: ${p => p.theme.fontSizes['2xl']};
  font-weight: ${p => p.theme.fontWeights.bold};
  color: ${p => p.theme.colors.textPrimary};
`;

const KPISub = styled.div`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textSecondary};
  margin-top: 2px;
`;

// Chart layout
const ChartRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.lg};
  @media (max-width: 1100px) { grid-template-columns: 1fr; }
`;

const ChartCard = styled.div`
  background: ${p => p.theme.colors.cardBackground};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.lg};
  box-shadow: ${p => p.theme.shadows.md};
  border: 1px solid ${p => p.theme.colors.border};
`;

const ChartHeader = styled.div`margin-bottom: ${p => p.theme.spacing.md};`;

const ChartTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${p => p.theme.fontSizes.base};
  font-weight: ${p => p.theme.fontWeights.semibold};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 2px 0;
`;

const ChartSubtitle = styled.p`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textSecondary};
  margin: 0;
`;

// Device breakdown
const DeviceRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${p => p.theme.spacing.md};
`;

const DevicePie = styled.div`text-align: center;`;

const DevicePieLabel = styled.div`
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: ${p => p.theme.fontWeights.semibold};
  color: ${p => p.theme.colors.textSecondary};
  margin-bottom: 8px;
`;

// Heatmap
const HeatmapWrap = styled.div`overflow-x: auto;`;

const HeatmapTable = styled.table`
  border-collapse: collapse;
  font-size: 11px;
  width: 100%;
`;

const HeatTh = styled.th`
  padding: 2px 4px;
  text-align: center;
  color: #6b7280;
  font-weight: 500;
  white-space: nowrap;
`;

const HeatCell = styled.td`
  width: 24px;
  height: 14px;
  border: 1px solid #f3f4f6;
  cursor: default;
  transition: opacity 0.1s;
  &:hover { outline: 2px solid #008080; }
`;

// Responder grid
const SectionTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${p => p.theme.fontSizes.lg};
  font-weight: ${p => p.theme.fontWeights.semibold};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0 0 ${p => p.theme.spacing.sm} 0;
`;

const GridNote = styled.p`
  font-size: ${p => p.theme.fontSizes.sm};
  color: #374151;
  font-weight: 500;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  padding: 6px 12px;
  border-radius: 6px;
  margin: 0 0 ${p => p.theme.spacing.sm} 0;
`;

const GridWrap = styled.div`
  height: 420px;
  margin-bottom: ${p => p.theme.spacing.xl};
`;

// Help icon
const HelpWrap = styled.span`position: relative; display: inline-flex; align-items: center;`;

const HelpTooltip = styled.div`
  position: absolute;
  bottom: 120%;
  left: 50%;
  transform: translateX(-50%);
  background: #1f2937;
  color: white;
  font-size: 12px;
  padding: 6px 10px;
  border-radius: 6px;
  white-space: normal;
  min-width: 200px;
  max-width: 280px;
  z-index: 9999;
  pointer-events: none;
  line-height: 1.4;
`;

// Status badge
const BadgeWrap = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
  white-space: nowrap;
`;

// Slide-in panel (Level 2)
const SlidePanel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 680px;
  max-width: 95vw;
  height: 100vh;
  background: white;
  box-shadow: -4px 0 24px rgba(0,0,0,0.18);
  z-index: 8000;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  padding: ${p => p.theme.spacing.lg};
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  margin-bottom: ${p => p.theme.spacing.md};
  flex-wrap: wrap;
`;

const PanelBack = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  padding: 4px 10px;
  cursor: pointer;
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textSecondary};
  &:hover { background: #f3f4f6; }
`;

const PanelTitle = styled.h3`
  font-size: ${p => p.theme.fontSizes.base};
  font-weight: ${p => p.theme.fontWeights.semibold};
  color: ${p => p.theme.colors.panelText};
  margin: 0;
`;

const PanelHelp = styled.p`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.panelMuted};
  margin: 0 0 ${p => p.theme.spacing.sm} 0;
`;

const PanelEmpty = styled.p`
  color: ${p => p.theme.colors.panelMuted};
  font-size: ${p => p.theme.fontSizes.sm};
  padding: ${p => p.theme.spacing.lg};
  text-align: center;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${p => p.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  padding: 4px;
  border-radius: 4px;
  &:hover { background: #f3f4f6; }
`;


const MomentTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${p => p.theme.fontSizes.sm};
`;

const DayTh = styled.th`
  text-align: left;
  padding: 6px 8px;
  background: #f9fafb;
  border-bottom: 1px solid ${p => p.theme.colors.border};
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: ${p => p.theme.fontWeights.semibold};
  color: ${p => p.theme.colors.textSecondary};
`;

const DayTd = styled.td`
  padding: 6px 8px;
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.panelBody};
  border-bottom: 1px solid #f3f4f6;
`;

// Day Overview Modal
const ModalOverlayWrap = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9500;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalBg = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.65);
`;

const ModalBox = styled.div`
  position: relative;
  background: white;
  border-radius: ${p => p.theme.borderRadius.xl};
  padding: ${p => p.theme.spacing.lg};
  width: 90%;
  max-width: 700px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0,0,0,0.25);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  margin-bottom: ${p => p.theme.spacing.md};
`;

const ModalTitle = styled.h3`
  font-size: ${p => p.theme.fontSizes.lg};
  font-weight: ${p => p.theme.fontWeights.semibold};
  color: ${p => p.theme.colors.panelText};
  margin: 0;
  flex: 1;
`;

const OverviewTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${p => p.theme.fontSizes.sm};
`;

const LinkBtn = styled.button`
  background: none;
  border: none;
  color: ${TEAL};
  cursor: pointer;
  font-size: ${p => p.theme.fontSizes.sm};
  padding: 0;
  &:hover { text-decoration: underline; }
`;
